import React, {useState, useEffect, useRef} from 'react';
import styles from './Navbar.module.css';
import { useAuthState } from 'react-firebase-hooks/auth';
import { signOut } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import { useNavigate } from 'react-router-dom';
import { useCredits } from '../contexts/CreditContext';
import { GAUtils } from '../model/GAUtils';

// 全局事件监听器
const accessEventTarget = new EventTarget();

export function emitAccessUpdate() {
    accessEventTarget.dispatchEvent(new Event('access-updated'));
}

const Navbar: React.FC = () => {
    const [user, loading] = useAuthState(auth);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const { credits, loading: creditsLoading } = useCredits();

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowDropdown(false);
            }
        };

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown]);

    const handleSignOut = async () => {
        try {
            // Track logout event
            GAUtils.trackLogout(user?.uid);
            await signOut(auth);
            setShowDropdown(false);
            navigate('/login');
        } catch (error) {
            console.error("Error signing out:", error);
        }
    };

    const handleLogoClick = () => {
        GAUtils.trackLogoClick();
    };

    const handleDropdownToggle = () => {
        if (!showDropdown) {
            GAUtils.trackDropdownOpen();
        }
        setShowDropdown(!showDropdown);
    };

    const handleBalanceClick = () => {
        GAUtils.trackNavClick('balance');
        setShowDropdown(false);
        navigate('/balance');
    };

    const getInitials = (name: string | null | undefined, email: string | null | undefined): string => {
        if (name) {
            const parts = name.split(' ');
            if (parts.length >= 2) {
                return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
            }
            return name.substring(0, 2).toUpperCase();
        }
        if (email) {
            return email.substring(0, 2).toUpperCase();
        }
        return 'U';
    };

    return (
        <div className={styles.navbar}>
            {/* Logo Section */}
            <div className={styles.navbarLogo}>
                🌐
                <a href="/" className={styles.navbarHome} onClick={handleLogoClick}>
                    XTEA
                </a>
            </div>

            {/* Menu Section */}
            <div className={styles.navbarMenu}>
                {!loading && user && (
                    <div className={styles.userStatus} ref={dropdownRef}>
                        <div 
                            className={styles.userInfo}
                            onClick={handleDropdownToggle}
                        >
                            <div className={styles.userAvatarWrapper}>
                                {user.photoURL ? (
                                    <img 
                                        src={user.photoURL} 
                                        alt={user.displayName || 'User'} 
                                        className={styles.userAvatar}
                                    />
                                ) : (
                                    <div className={styles.userAvatarFallback}>
                                        {getInitials(user.displayName, user.email)}
                                    </div>
                                )}
                                <span className={styles.userStatusDot}></span>
                            </div>
                            <div className={styles.userDetails}>
                                <span className={styles.userName}>
                                    {user.displayName || 'User'}
                                </span>
                                <span className={styles.userRole}>
                                    {user.email}
                                </span>
                            </div>
                        </div>

                        {showDropdown && (
                            <div className={styles.userDropdown}>
                                <div className={styles.dropdownHeader}>
                                    <span>Signed in as</span>
                                    <strong>{user.email}</strong>
                                </div>
                                <div className={styles.dropdownDivider}></div>
                                <button 
                                    className={styles.balanceButton}
                                    onClick={handleBalanceClick}
                                >
                                    <span>💰</span>
                                    <span>Credits: {creditsLoading ? '...' : credits}</span>
                                </button>
                                <div className={styles.dropdownDivider}></div>
                                <button 
                                    className={styles.signOutButton}
                                    onClick={handleSignOut}
                                >
                                    <span>🚪</span>
                                    <span>Sign Out</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Navbar;
